import { Editor } from '@ckeditor/ckeditor5-core';


enum CKEditorOperationKeysEnum {
    fontFamily = 'fontFamily',
    fontSize = 'fontSize',
    heading = 'heading',
    lineHeight = 'lineHeight',
    bold = 'bold',
    italic = 'italic',
    underline = 'underline',
    strikethrough = 'strikethrough',
    fontColor = 'agprFontColor',
    fontBackgroundColor = 'agprFontBackgroundColor',
    subscript = 'subscript',
    superscript = 'superscript',
    numberedList = 'numberedList',
    bulletedList = 'bulletedList',
    table = 'table',
    alignment = 'alignment',
    decreaseIndent = 'outdent',
    increaseIndent = 'indent',
    language = 'language',
    blockQuote = 'blockQuote',
    link = 'agprLink',
    image = 'imageUpload',
    removeFormat = 'removeFormat',
    undo = 'undo',
    redo = 'redo',
    sourceEditing = 'sourceEditing',
    personalizations = 'agprPersonalizations',
    translationDropdown = 'agprTranslationDropdown',
    translationButton = 'agprTranslationButtonn',
    prCoPilot = 'agprPrCoPilot'
};

export class CKEditorAccessibilityHelper {

    static dropdownInlineStyles = ['fontFamily', 'fontSize'];
    static dropdownBlockStyles = ['heading', 'lineHeight', 'alignment'];
    static openDialogItems = [
        CKEditorOperationKeysEnum.fontColor,
        CKEditorOperationKeysEnum.fontBackgroundColor,
        CKEditorOperationKeysEnum.link,
        CKEditorOperationKeysEnum.prCoPilot
    ];
    static keyStrokesToButtonsMap: Map<string, CKEditorOperationKeysEnum> = new Map([
        ['ALT+Z', CKEditorOperationKeysEnum.fontFamily],
        ['ALT+X', CKEditorOperationKeysEnum.fontSize],
        ['ALT+C', CKEditorOperationKeysEnum.lineHeight],
        ['ALT+V', CKEditorOperationKeysEnum.heading],
        ['ALT+T', CKEditorOperationKeysEnum.fontColor],
        ['ALT+B', CKEditorOperationKeysEnum.fontBackgroundColor],
        ['ALT+A', CKEditorOperationKeysEnum.alignment],
        ['ALT+L', CKEditorOperationKeysEnum.link],
        ['CTRL+BACKSPACE', CKEditorOperationKeysEnum.removeFormat]
    ]);

    public static applyAccessibilityFix(editor: Editor, toolbarConfigItems: any[], escapeEditorCallback: any): void {
        editor.model.document.selection.on('change', (event, data) => {
            requestAnimationFrame(() => {
                this.associateDropdownValueWithDropdownControl(editor);
            });
        });
        this.setKeyboardNavigation(editor, toolbarConfigItems);
        this.bindEscapeEditor(editor, escapeEditorCallback);
        editor.editing.view.focus();
    }

    private static associateDropdownValueWithDropdownControl(editor: Editor): void {
        this.dropdownInlineStyles.forEach(style => {
            const dropdownButton = this.getDropdownButton(style);
            const value = editor.model.document.selection.getAttribute(style);
            if (dropdownButton && value) {
                dropdownButton.removeAttribute('aria-labelledby');
                dropdownButton.setAttribute('aria-label',
                    style + (value ? `: ${value}` : '')
                );
            }
        });

        const parent = editor.model.document.selection.anchor?.parent;
        this.dropdownBlockStyles.forEach(style => {
            const dropdownButton = this.getDropdownButton(style);
            const value = this.getBlockStyleValue(parent, style);
            if (dropdownButton && value) {
                dropdownButton.removeAttribute('aria-labelledby');
                dropdownButton.setAttribute('aria-label',
                    style + (value ? `: ${value}` : '')
                );
            }
        });
    }

    private static getBlockStyleValue(parent: any, style: string): string {
        switch (style) {
            case 'heading':
                return parent.name;
            case 'lineHeight':
            case 'alignment':
                return parent._attrs?.get(style) || '';
            default:
                return '';
        }
    }

    private static setKeyboardNavigation(editor: Editor, toolbarConfigItems: string[]): void {
        let lastOperationIndex = 0;
        for (let i = 0; i < (editor.ui.view as any).toolbar.items.length; i++) {
            let item = (editor.ui.view as any).toolbar.items.get(i);
            // image upload button
            if (item.acceptedType) {
                item = item.buttonView;
            }
            item.on('execute', () => {
                lastOperationIndex = i;
                // if a dialog will opens up, we don't change focus
                if (!this.openDialogItems.includes(toolbarConfigItems[i] as CKEditorOperationKeysEnum)) {
                    requestAnimationFrame(() => {
                        this.focusToolbarButton(editor, lastOperationIndex);
                    });
                }
            });
        }
        // set our own shortcuts
        this.keyStrokesToButtonsMap.forEach((value: CKEditorOperationKeysEnum, key: string) => {
            editor.keystrokes.set(key, () => {
                // this won't work if we want to add shortcut to language dropdown, because that toolbar item is an object instead of a string
                lastOperationIndex = toolbarConfigItems.findIndex(x => x === value);
                if (lastOperationIndex > -1) {
                    this.focusToolbarButton(editor, lastOperationIndex);
                }
            });
        });

        editor.keystrokes.set('TAB', (event, cancel) => {
            if (!(editor.ui.view as any).toolbar.element.contains(document.activeElement)
                && !(editor.plugins.get('SourceEditing') as any).isSourceEditingMode) {
                editor.execute('indent');
                cancel();
            }
        });
        editor.keystrokes.set('SHIFT+TAB', (event, cancel) => {
            if (!(editor.ui.view as any).toolbar.element.contains(document.activeElement)
                && !(editor.plugins.get('SourceEditing') as any).isSourceEditingMode) {
                editor.execute('outdent');
                cancel();
            }
        });
        editor.keystrokes.set('ALT+F10', (event, cancel) => {
            this.focusToolbarButton(editor, lastOperationIndex);
            cancel();
        }, { priority: 'low' });
    }

    private static focusToolbarButton(editor: Editor, operationIndex: number): void {
        let toolbarItem = (editor.ui.view as any).toolbar.items.get(operationIndex);
        if (toolbarItem) {
            if (toolbarItem.buttonView) {
                toolbarItem = toolbarItem.buttonView;
            }
            toolbarItem.element?.focus();
        }
    }

    private static getDropdownButton(style: string): HTMLElement {
        switch (style) {
            case CKEditorOperationKeysEnum.fontFamily:
                return document.querySelector('.ck-font-family-dropdown > button');
            case CKEditorOperationKeysEnum.fontSize:
                return document.querySelector('.ck-font-size-dropdown > button');
            case CKEditorOperationKeysEnum.lineHeight:
                return document.querySelector('.ck-line-height-dropdown > button');
            case CKEditorOperationKeysEnum.heading:
                return document.querySelector('.ck-heading-dropdown > button');
            case CKEditorOperationKeysEnum.fontColor:
                return document.querySelectorAll('.ck-color-ui-dropdown > button')?.[0] as HTMLElement;
            case CKEditorOperationKeysEnum.fontBackgroundColor:
                return document.querySelectorAll('.ck-color-ui-dropdown > button')?.[1] as HTMLElement;
            case CKEditorOperationKeysEnum.alignment:
                return document.querySelector('.ck-alignment-dropdown > button');
            default:
                return null;
        }
    }

    private static bindEscapeEditor(editor: Editor, escapeEditorCallback: any): void {
        editor.keystrokes.set('ESC', (data, cancel) => {
            escapeEditorCallback();
            cancel();
        });
    }
}
